// Panel de acciones de ensayo — fuerza estados de la PROPIA cuenta.
//
// UX de seguridad estilo consola: cada acción se "arma" con el primer click
// (el botón cambia a CONFIRMAR con cuenta regresiva) y se ejecuta con el
// segundo. Todo queda auditado server-side (aparece en el panel de audit).

import { useEffect, useRef, useState } from 'react';
import {
  BadgeCheck,
  BadgeX,
  CalendarX2,
  DatabaseZap,
  MailX,
  ShieldOff,
  SquareX,
} from 'lucide-react';
import { runAction, type ActionResult, type InspectorAction } from '@/lib/api';
import { useI18n, type TFn } from '@/lib/i18n';
import { StatusLed } from './StatusLed';

interface ActionDef {
  action: InspectorAction;
  icon: typeof BadgeCheck;
  /** Acciones que dejan la cuenta en un estado "roto" a propósito. */
  destructive?: boolean;
}

const ACTIONS: ActionDef[] = [
  { action: 'email_verify', icon: BadgeCheck },
  { action: 'email_unverify', icon: BadgeX, destructive: true },
  { action: 'subscriptions_cancel', icon: SquareX, destructive: true },
  { action: 'subscriptions_expire', icon: CalendarX2, destructive: true },
  { action: 'email_tokens_expire', icon: MailX, destructive: true },
  { action: 'entitlements_cache_invalidate', icon: DatabaseZap },
  { action: 'sessions_revoke_all', icon: ShieldOff, destructive: true },
];

const ARM_TIMEOUT_MS = 4000;

function describeResult(t: TFn, r: ActionResult): string {
  switch (r.action) {
    case 'email_verify':
      return t('act.r.emailVerified');
    case 'email_unverify':
      return t('act.r.emailUnverified');
    case 'subscriptions_cancel':
    case 'subscriptions_expire':
      return t('act.r.subs', { n: r['affected'] as number, status: r['forced_status'] as string });
    case 'sessions_revoke_all':
      return t('act.r.sessions', { n: r['sessions_revoked'] as number });
    case 'email_tokens_expire':
      return t('act.r.tokens', { n: r['tokens_expired'] as number });
    case 'entitlements_cache_invalidate':
      return t('act.r.cache', { partners: (r['partners'] as string[]).join(', ') });
    case 'webhook_simulate':
      // No disparado desde este panel (vive en WebhookSimPanel) — caso
      // presente solo para agotar el union de InspectorAction.
      return t('wh.r.sent', { event: String(r['webhook_event']), id: String(r['event_id']) });
    case 'grace_set':
    case 'grace_expire':
    case 'grace_clear':
      // Idem: se disparan desde GracePanel.
      return t('act.r.grace');
  }
}

export function ActionsPanel({
  onExecuted,
  onSelfRevoked,
  className = 'col-span-12',
}: {
  /** Refrescar los datos del dashboard tras una acción exitosa. */
  onExecuted: () => void;
  /** La revocación total mató también esta sesión — el caller hace logout. */
  onSelfRevoked: () => void;
  /** Ancho en la grilla del dashboard. Lo decide el caller, no el panel. */
  className?: string;
}) {
  const { t, locale } = useI18n();
  const [armed, setArmed] = useState<InspectorAction | null>(null);
  const [busy, setBusy] = useState<InspectorAction | null>(null);
  const [log, setLog] = useState<{ ok: boolean; text: string; at: string }[]>([]);
  const disarmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (disarmTimer.current) clearTimeout(disarmTimer.current);
  }, []);

  function arm(action: InspectorAction) {
    setArmed(action);
    if (disarmTimer.current) clearTimeout(disarmTimer.current);
    disarmTimer.current = setTimeout(() => setArmed(null), ARM_TIMEOUT_MS);
  }

  async function fire(def: ActionDef) {
    setArmed(null);
    setBusy(def.action);
    const at = new Date().toLocaleTimeString(locale, { hour12: false });
    try {
      const result = await runAction(def.action);
      setLog((l) => [{ ok: true, text: describeResult(t, result), at }, ...l].slice(0, 6));
      if (def.action === 'sessions_revoke_all') {
        onSelfRevoked();
        return;
      }
      onExecuted();
    } catch (err) {
      setLog((l) =>
        [
          {
            ok: false,
            text: `${t(`act.${def.action}`)}: ${err instanceof Error ? err.message : 'error'}`,
            at,
          },
          ...l,
        ].slice(0, 6),
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      aria-label={t('t.actions')}
      className={`${className} rounded border border-status-warning/30 bg-surface`}
    >
      <header className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 border-b border-status-warning/30 bg-surface-raised px-3 py-1.5">
        <h2 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-status-warning">
          <StatusLed tone="warning" />
          {t('t.actions')} — {t('act.subtitle')}
        </h2>
        <span className="text-[10px] text-ink-subtle">{t('act.help')}</span>
      </header>
      <div className="flex flex-wrap gap-2 p-3">
        {ACTIONS.map((def) => {
          const Icon = def.icon;
          const label = t(`act.${def.action}`);
          const isArmed = armed === def.action;
          const isBusy = busy === def.action;
          return (
            <button
              key={def.action}
              type="button"
              title={t(`act.${def.action}.hint`)}
              disabled={busy !== null}
              onClick={() => (isArmed ? void fire(def) : arm(def.action))}
              className={`flex min-h-[2.25rem] items-center gap-1.5 rounded border px-2.5 py-1 text-[11px] uppercase tracking-wider transition-colors disabled:opacity-40 ${
                isArmed
                  ? 'border-status-error bg-status-error/15 text-status-error'
                  : def.destructive
                    ? 'border-surface-border-strong text-ink-muted hover:border-status-warning hover:text-status-warning'
                    : 'border-surface-border-strong text-ink-muted hover:border-brand hover:text-brand'
              }`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {isBusy
                ? t('act.running')
                : isArmed
                  ? t('act.confirm', { label: label.toLowerCase() })
                  : label}
            </button>
          );
        })}
      </div>
      {log.length > 0 && (
        <ul className="border-t border-surface-border px-3 py-2 text-[11px]" aria-live="polite">
          {log.map((entry, i) => (
            <li key={`${entry.at}-${i}`} className="flex items-center gap-2 py-0.5">
              <StatusLed tone={entry.ok ? 'success' : 'error'} />
              <span className="tabular-nums text-ink-subtle">{entry.at}</span>
              <span className={entry.ok ? 'text-ink-muted' : 'text-status-error'}>{entry.text}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
